import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { streamUrl, BACKEND_URL, api } from "../lib/api";
import { saveContinueState } from "../lib/continueListening";
import { noteRecentlyPlayed, recentlyPlayedIds } from "../lib/recentlyPlayed";
import {
  loadPrefs, savePrefs, presetToPrefs, clampPrefs,
  playbackRateFromA4, DEFAULT_PREFS, SKIP_FADE_SECONDS,
} from "../lib/frequencyPrefs";
import {
  startToneLayer, stopToneLayer, setToneLayerVolume, resumeAudioContextOnGesture,
} from "../lib/webAudio";

const PlayerContext = createContext(null);

// Per-listener commercial cadence: insert one ≤15s sponsor break every 75 minutes
// of cumulative playtime. Counted only while a real (non-commercial) track is
// actually playing; paused / silent time does not count. Inserted ONLY at clean
// boundaries (track-ended event) so an active track is never interrupted.
const COMMERCIAL_THRESHOLD_SECONDS = 75 * 60;

// Phase B.1 — Equal-power crossfade between two `<audio>` decks.
// Outgoing volume = master * cos(progress * π/2), incoming = master * sin(...).
// Crossfade only fires when BOTH outgoing AND incoming are audio tracks (not
// MP4 video). Video transitions hard-cut for v1 — visual continuity matters
// more than audio overlap there. Skip uses a deliberately shorter fade so
// manual skips stay snappy. A `crossfade_seconds=0` setting hard-cuts always.
const CROSSFADE_FRAME_MS = 16; // ~60fps via setInterval (RAF pauses on hidden tabs; we want it to keep running for OS lock-screen handover).

const ERROR_CODES = { 1: "ABORTED", 2: "NETWORK", 3: "DECODE", 4: "SRC_NOT_SUPPORTED" };

function friendlyError(code, mediaType) {
  if (code === 4) {
    return mediaType === "video"
      ? "This MP4 was uploaded, but the browser can't play this video format. Please upload H.264/AAC MP4 or MP3."
      : "This audio format isn't supported by your browser. Please upload MP3, M4A, or WAV.";
  }
  if (code === 3) return "This file appears corrupt and cannot be decoded.";
  if (code === 2) return "Network error while loading this track.";
  return "Could not play this track.";
}

export function PlayerProvider({ children }) {
  // Three refs:
  //   audioRef — <audio> for MP3/M4A/WAV (best mobile background support)
  //   videoRef — <video> for full music videos (.mp4 with embedded audio)
  //   coverRef — <video muted loop> animated cover (visual layer only)
  // Audio decks (Phase B.1 crossfade): two `<audio>` elements that alternate
  // ownership of `currentTrack`. `activeAudioChannelRef` holds 'A' or 'B' and
  // identifies which deck is currently the "current" one. Video tracks still
  // use a single shared video element (`videoRef`) — video↔video crossfade is
  // out of scope for v1 (and visually awkward).
  const audioARef = useRef(null);
  const audioBRef = useRef(null);
  const activeAudioChannelRef = useRef("A");
  const videoRef = useRef(null);
  const coverRef = useRef(null);
  const trackRef = useRef(null);
  const queueRef = useRef([]);
  const indexRef = useRef(0);
  const stopRequestedRef = useRef(false);  // Hard guard: blocks auto-next + auto-replay after Stop until user explicitly plays again.
  // Commercial cadence — see COMMERCIAL_THRESHOLD_SECONDS at top of file.
  const commercialAccumulatedRef = useRef(0);   // seconds of real-track playtime since last break
  const pendingCommercialRef = useRef(null);     // prefetched commercial doc waiting for next clean break
  // Phase B.3 prayer slot scheduler — when a slot's time arrives, this ref
  // holds {slot, track} until the next clean break splices it in.
  const pendingPrayerRef = useRef(null);
  const prayerFiredRef = useRef(new Set()); // slot-id+date dedupe — fire once per scheduled occurrence
  // Phase B.4 shofar broadcast — set when a new admin broadcast arrives.
  const pendingShofarRef = useRef(null);
  const shofarSeenRef = useRef(new Set());  // broadcast ids already consumed by this listener
  // Crossfade state machine.
  //   armed             — true once we've kicked off a fade for the current track.
  //   intervalId        — handle for the volume-ramp ticker.
  //   suppressEnded     — when an outgoing track fires `ended` AFTER we've
  //                       already advanced the queue, ignore that event so we
  //                       don't double-skip.
  //   suppressSrcLoad   — when we advance index AS PART OF a finishing fade,
  //                       the incoming track's src is already loaded on the
  //                       new active deck; tell the src effect to skip itself.
  const crossfadeRef = useRef({
    armed: false, intervalId: null, suppressEnded: false, suppressSrcLoad: false,
  });

  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(0.85);
  const [mode, setMode] = useState("normal");
  const [expanded, setExpanded] = useState(false);
  const [videoHidden, setVideoHidden] = useState(false);
  const [playbackError, setPlaybackError] = useState(null);
  const [needsTapToStart, setNeedsTapToStart] = useState(false);
  const [stationName, setStationName] = useState(null);
  const [stationId, setStationId] = useState(null);
  const [reactions, setReactions] = useState({}); // { trackId: 'like'|'dislike' }
  // "Up Next" preview — populated by an effect when remaining time on the
  // active track dips below ~5s, cleared whenever the active track changes.
  // Surfaced to the UI as a small fade-in pill near the now-playing meta.
  const [upNextTrack, setUpNextTrack] = useState(null);
  // Listening preferences (Frequency / Tuning)
  const [listeningPrefs, setListeningPrefs] = useState(DEFAULT_PREFS);
  // Active audio deck — paired with `activeAudioChannelRef` so callbacks have
  // sync access (ref) AND consumers re-render on channel swap (state).
  const [activeAudioChannel, setActiveAudioChannel] = useState("A");
  useEffect(() => { activeAudioChannelRef.current = activeAudioChannel; }, [activeAudioChannel]);
  // Optional external host where the main <video> element teleports to via portal
  // (e.g. /stations Now Playing hero claims this so the video plays in the hero slot).
  const [videoHost, setVideoHost] = useState(null);

  // Listen for station-set events from the StationsPage
  useEffect(() => {
    const handler = (e) => {
      setStationId(e.detail?.id || null);
      setStationName(e.detail?.name || null);
    };
    window.addEventListener("yahweh-station", handler);
    return () => window.removeEventListener("yahweh-station", handler);
  }, []);

  // Reset station context if user explicitly plays something via playTrack/playQueue with mode != station
  // (handled in those callbacks below)

  const currentTrack = queue[index] || null;
  useEffect(() => { trackRef.current = currentTrack; }, [currentTrack]);
  useEffect(() => { queueRef.current = queue; }, [queue]);
  useEffect(() => { indexRef.current = index; }, [index]);
  useEffect(() => { setVideoHidden(false); setExpanded(false); }, [currentTrack?.id]);

  // Helpers — deck-aware lookups for the dual-audio crossfade architecture.
  const getActiveAudio = useCallback(() => (
    activeAudioChannelRef.current === "B" ? audioBRef.current : audioARef.current
  ), []);
  const getInactiveAudio = useCallback(() => (
    activeAudioChannelRef.current === "B" ? audioARef.current : audioBRef.current
  ), []);
  // Active media element for the current track (audio deck OR video).
  const getActive = useCallback(() => {
    const t = trackRef.current;
    if (!t) return null;
    return t.media_type === "video" ? videoRef.current : getActiveAudio();
  }, [getActiveAudio]);

  // Common event-binding for audio + video elements. Both audio decks fire
  // events (so we can drive the visualizer + state from whichever is active);
  // we filter to the ACTIVE element so the inactive deck's chatter (e.g. the
  // pre-loaded incoming track during a fade) doesn't pollute global state.
  const bindEvents = useCallback((el) => {
    el.addEventListener("timeupdate", () => {
      // only update from the active element (avoid races between audio + video)
      if (el === getActive()) setCurrentTime(el.currentTime);
    });
    el.addEventListener("loadedmetadata", () => {
      if (el === getActive()) setDuration(el.duration || 0);
    });
    el.addEventListener("play", () => { if (el === getActive()) setIsPlaying(true); });
    el.addEventListener("pause", () => { if (el === getActive()) setIsPlaying(false); });
    el.addEventListener("ended", () => {
      if (el === getActive()) window.dispatchEvent(new CustomEvent("yahweh-track-ended"));
    });
    el.addEventListener("error", () => {
      if (el !== getActive()) return;
      const t = trackRef.current;
      const err = el.error;
      const code = err?.code;
      // eslint-disable-next-line no-console
      console.error("[Yahweh Player] media error", {
        code, type: ERROR_CODES[code] || "UNKNOWN", message: err?.message,
        track_id: t?.id, media_type: t?.media_type, src: el.currentSrc || el.src,
      });
      setPlaybackError(friendlyError(code, t?.media_type));
      setIsPlaying(false);
    });
    el.addEventListener("loadstart", () => { if (el === getActive()) setPlaybackError(null); });
    el.addEventListener("canplay", () => { if (el === getActive()) setPlaybackError(null); });
  }, [getActive]);

  const attachAudioA = useCallback((el) => {
    if (!el || audioARef.current === el) return;
    audioARef.current = el;
    el.volume = volume;
    bindEvents(el);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const attachAudioB = useCallback((el) => {
    if (!el || audioBRef.current === el) return;
    audioBRef.current = el;
    el.volume = 0; // inactive deck starts silent
    bindEvents(el);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Backwards-compat alias — older code (and any external attach via ref) still
  // works against the A deck.
  const attachAudio = attachAudioA;
  const attachVideo = useCallback((el) => {
    if (!el || videoRef.current === el) return;
    videoRef.current = el;
    el.volume = volume;
    bindEvents(el);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const attachCover = useCallback((el) => {
    if (!el || coverRef.current === el) return;
    coverRef.current = el;
    el.muted = true;
    el.loop = true;
    el.playsInline = true;
    el.addEventListener("error", () => {
      // Cover failure is non-fatal — fall back to static cover silently
      try { el.removeAttribute("src"); el.load(); } catch (_) {}
      // eslint-disable-next-line no-console
      console.warn("[Yahweh Player] cover-video error (falling back to static cover)", {
        track_id: trackRef.current?.id, src: el.currentSrc || el.src,
      });
    });
  }, []);

  // Helper to compute the streaming URL for a track (handles commercial routing).
  const trackToSrc = useCallback((t) => {
    if (!t) return null;
    if (t.is_commercial) return `${BACKEND_URL}/api/commercials/${t.commercial_id}/stream`;
    if (t.is_shofar) return t.stream_url_override
      ? `${BACKEND_URL}${t.stream_url_override}`
      : `${BACKEND_URL}/api/shofar/${t.shofar_clip_id}/stream`;
    if (t.is_prayer && t.backing_track_id) return streamUrl(t.backing_track_id);
    return streamUrl(t.id);
  }, []);

  // Load src for the right element on track change. Skipped when the crossfade
  // orchestrator already loaded the incoming track on the new active deck —
  // otherwise we'd double-load and the audible result would jump back to t=0.
  useEffect(() => {
    if (crossfadeRef.current.suppressSrcLoad) {
      crossfadeRef.current.suppressSrcLoad = false;
      return;
    }
    const audioA = audioARef.current;
    const audioB = audioBRef.current;
    const video = videoRef.current;
    if ((!audioA && !audioB) || !video) return;
    setPlaybackError(null);
    setNeedsTapToStart(false);

    if (!currentTrack || !currentTrack.id) {
      [audioA, audioB].forEach((el) => {
        if (!el) return;
        try { el.pause(); el.removeAttribute("src"); el.load(); } catch (_) {}
      });
      try { video.pause(); video.removeAttribute("src"); video.load(); } catch (_) {}
      return;
    }

    const isVideoTrack = currentTrack.media_type === "video";
    const activeAudio = getActiveAudio();
    const inactiveAudio = getInactiveAudio();

    // Hard cut: clear ALL non-target elements (the inactive audio deck included)
    // so a leftover crossfade tail doesn't bleed into the new track.
    if (isVideoTrack) {
      [audioA, audioB].forEach((el) => {
        if (!el) return;
        try { el.pause(); el.removeAttribute("src"); el.load(); el.volume = 0; } catch (_) {}
      });
    } else {
      try { video.pause(); video.removeAttribute("src"); video.load(); } catch (_) {}
      if (inactiveAudio) {
        try { inactiveAudio.pause(); inactiveAudio.removeAttribute("src"); inactiveAudio.load(); inactiveAudio.volume = 0; } catch (_) {}
      }
    }

    const target = isVideoTrack ? video : activeAudio;
    if (!target) return;
    try {
      target.src = trackToSrc(currentTrack);
      target.volume = volume; // restore master volume on the now-active deck
      target.load();
      const tryPlay = (attempt = 0) => {
        const p = target.play();
        if (p && typeof p.then === "function") {
          p.catch((err) => {
            // NotAllowedError → real autoplay block, need a tap. Other
            // errors (NotSupportedError, AbortError on rapid src changes,
            // transient buffer-not-ready) often clear on a short retry.
            // Retry up to 2x at 350ms each before surfacing tap-to-start.
            const name = err?.name || "";
            if (name === "NotAllowedError") {
              // eslint-disable-next-line no-console
              console.warn("[Yahweh Player] autoplay blocked");
              setNeedsTapToStart(true);
              return;
            }
            if (attempt < 2) {
              // eslint-disable-next-line no-console
              console.warn("[Yahweh Player] play() rejected, retrying", { attempt, name });
              setTimeout(() => tryPlay(attempt + 1), 350);
              return;
            }
            // eslint-disable-next-line no-console
            console.warn("[Yahweh Player] play() failed after retries", name);
            setNeedsTapToStart(true);
          });
        }
      };
      tryPlay(0);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[Yahweh Player] failed to set src", e);
      setPlaybackError("Could not start playback.");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTrack?.id]);

  // Cover video src on track change. Triggered ONLY by id changes — using
  // currentTrack object identity caused spurious cover reloads whenever any
  // upstream re-fetch produced an equivalent-but-different object reference
  // (e.g. an admin edit refreshing the inventory list mid-playback).
  useEffect(() => {
    const el = coverRef.current;
    if (!el) return;
    if (currentTrack?.has_cover_video && currentTrack?.media_type !== "video") {
      el.src = `${BACKEND_URL}/api/tracks/${currentTrack.id}/cover-video`;
      el.load();
      el.play().catch(() => {});
    } else {
      el.pause();
      el.removeAttribute("src");
      el.load();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTrack?.id, currentTrack?.has_cover_video, currentTrack?.media_type]);

  // Volume on the ACTIVE audio deck + video; inactive deck stays at whatever
  // gain the crossfade animator wrote (default 0). Don't stomp it.
  useEffect(() => {
    const a = getActiveAudio();
    const v = videoRef.current;
    if (a) a.volume = volume;
    if (v) v.volume = volume;
  }, [volume, getActiveAudio]);

  // Sync cover playback to main play state + videoHidden
  useEffect(() => {
    const cover = coverRef.current;
    if (!cover || !cover.src) return;
    if (videoHidden) { cover.pause(); return; }
    if (isPlaying) cover.play().catch(() => {});
    else cover.pause();
  }, [isPlaying, videoHidden]);

  const playTrack = useCallback((track) => {
    if (!track || !track.id) return;
    stopRequestedRef.current = false;
    setQueue([track]); setIndex(0); setMode("normal");
  }, []);

  // Resume a single track at a given position. Used by Continue Listening.
  const playTrackAt = useCallback((track, positionSeconds = 0) => {
    if (!track || !track.id) return;
    stopRequestedRef.current = false;
    setQueue([track]); setIndex(0); setMode("normal");
    // Seek once the active element has metadata. Try a few times to handle
    // both cached and slow loads.
    let attempts = 0;
    const trySeek = () => {
      const el = getActive();
      if (el && el.currentSrc && (el.readyState >= 1 || (el.duration && !isNaN(el.duration)))) {
        try { el.currentTime = Math.max(0, positionSeconds); } catch (_) {}
        const p = el.play();
        if (p && typeof p.then === "function") p.catch(() => setNeedsTapToStart(true));
        return;
      }
      attempts += 1;
      if (attempts < 20) setTimeout(trySeek, 150);
    };
    setTimeout(trySeek, 150);
  }, [getActive]);

  const playQueue = useCallback((tracks, startIndex = 0, newMode = "normal") => {
    if (!Array.isArray(tracks) || tracks.length === 0) return;
    stopRequestedRef.current = false;
    setQueue(tracks);
    setIndex(Math.max(0, Math.min(startIndex, tracks.length - 1)));
    setMode(newMode);
    if (newMode !== "station") {
      setStationId(null);
      setStationName(null);
    }
  }, []);

  const next = useCallback(() => {
    setIndex((i) => (queue.length === 0 ? 0 : (i + 1) % queue.length));
  }, [queue.length]);

  const prev = useCallback(() => {
    setIndex((i) => (queue.length === 0 ? 0 : (i - 1 + queue.length) % queue.length));
  }, [queue.length]);

  useEffect(() => {
    const handler = () => {
      // Stop guard: if user pressed Stop, do not auto-advance.
      if (stopRequestedRef.current) return;
      // Crossfade already advanced for us — the late `ended` from the now-
      // muted outgoing deck is just noise.
      if (crossfadeRef.current.suppressEnded) {
        crossfadeRef.current.suppressEnded = false;
        return;
      }

      const justEnded = trackRef.current;
      const q = queueRef.current;
      const i = indexRef.current;

      // (1) A commercial just ended — reset the cadence counter, splice the
      // commercial out of the queue, and continue with the next real track.
      if (justEnded?.is_commercial) {
        commercialAccumulatedRef.current = 0;
        pendingCommercialRef.current = null;
        const idx = q.findIndex((x) => x.id === justEnded.id);
        if (idx === -1) { next(); return; }
        const newQ = [...q.slice(0, idx), ...q.slice(idx + 1)];
        setQueue(newQ);
        if (newQ.length === 0) { setIndex(0); return; }
        // After removal, queue[idx] is the next real track. Wrap if necessary.
        setIndex(idx >= newQ.length ? 0 : idx);
        return;
      }

      // (2) A real track just ended AND a commercial is queued for insertion.
      // Splice it in at index+1 and jump to it. Pending ref is cleared on
      // commercial-end so the threshold counter resets fresh.
      if (pendingCommercialRef.current) {
        const data = pendingCommercialRef.current;
        const commercialTrack = {
          id: `commercial-${data.id}-${Date.now()}`,
          commercial_id: data.id,
          title: `${data.sponsor} · sponsor message`,
          artist: data.cta || "Yah-Wave Radio sponsor",
          media_type: data.media_type || "audio",
          is_commercial: true,
          has_cover: false,
          has_cover_video: false,
          duration_seconds: data.duration_seconds,
          sponsor_url: data.url,
        };
        const insertIdx = i + 1;
        const newQ = [...q.slice(0, insertIdx), commercialTrack, ...q.slice(insertIdx)];
        setQueue(newQ);
        setIndex(insertIdx);
        return;
      }

      next();
    };
    window.addEventListener("yahweh-track-ended", handler);
    return () => window.removeEventListener("yahweh-track-ended", handler);
  }, [next]);

  // ---- Commercial cadence: accumulate listening time + prefetch a sponsor ----
  // Tick once per second while a real (non-commercial) track is playing. When
  // we cross the 75-minute threshold, asynchronously fetch ONE active sponsor
  // matching the current station so we can splice it in at the next clean
  // track-end boundary (handled in the `yahweh-track-ended` listener above).
  // If no commercial is available we back off ~60s before retrying so we don't
  // hammer the endpoint.
  useEffect(() => {
    if (!isPlaying) return undefined;
    const id = setInterval(async () => {
      const t = trackRef.current;
      if (!t || t.is_commercial || t.is_prayer || t.is_shofar) return;
      commercialAccumulatedRef.current += 1;
      if (
        commercialAccumulatedRef.current >= COMMERCIAL_THRESHOLD_SECONDS &&
        !pendingCommercialRef.current
      ) {
        try {
          const { api: apiClient } = await import("../lib/api");
          const params = stationId ? { station: stationId } : {};
          const resp = await apiClient.get("/commercials/next", { params });
          if (resp.status === 204 || !resp.data || !resp.data.id) {
            // No commercial available right now — back off so we retry shortly.
            commercialAccumulatedRef.current = COMMERCIAL_THRESHOLD_SECONDS - 60;
          } else {
            pendingCommercialRef.current = resp.data;
          }
        } catch (_) {
          commercialAccumulatedRef.current = COMMERCIAL_THRESHOLD_SECONDS - 60;
        }
      }
    }, 1000);
    return () => clearInterval(id);
  }, [isPlaying, stationId]);

  // ---- Phase B.3: Prayer slot scheduler ----
  // Polls /api/schedule/today every minute, and ticks every 30s comparing
  // current Chicago time against active slots. When a slot's time passes
  // (within a 60s grace window) AND the slot has a track AND we haven't
  // already fired this slot today, set pendingPrayerRef so the next clean
  // break splices it in.
  useEffect(() => {
    let cancelled = false;
    let slots = [];
    const fetchSlots = async () => {
      try {
        const { data } = await api.get("/schedule/today");
        if (cancelled) return;
        slots = (data?.slots || []).filter((s) => s.active && s.track);
      } catch { /* keep last known */ }
    };
    const tick = () => {
      if (cancelled || pendingPrayerRef.current) return;
      try {
        const nowStr = new Date().toLocaleTimeString("en-US", {
          timeZone: "America/Chicago", hour: "2-digit", minute: "2-digit", hour12: false,
        });
        const ymd = new Date().toLocaleDateString("en-CA", { timeZone: "America/Chicago" });
        const [h, m] = nowStr.split(":").map((x) => parseInt(x, 10));
        const cur = h * 60 + m;
        for (const s of slots) {
          const [sh, sm] = (s.time_chicago || "00:00").split(":").map((x) => parseInt(x, 10));
          const slotMin = sh * 60 + sm;
          // 60-second grace window so a slow tick doesn't miss the firing.
          const within = cur >= slotMin && cur - slotMin <= 1;
          const fireKey = `${s.id}:${ymd}`;
          if (within && !prayerFiredRef.current.has(fireKey)) {
            prayerFiredRef.current.add(fireKey);
            pendingPrayerRef.current = { slot: s, track: s.track, fire_key: fireKey };
            break;
          }
        }
      } catch { /* swallow — scheduler is best-effort */ }
    };

    fetchSlots();
    const tickId = setInterval(tick, 30 * 1000);
    const fetchId = setInterval(fetchSlots, 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(tickId);
      clearInterval(fetchId);
    };
  }, []);

  // ---- Phase B.4: Shofar broadcast poller ----
  // Polls /api/shofar/active-broadcast every 15s. When a NEW broadcast id
  // arrives, set pendingShofarRef so the next clean break splices it in.
  // Each listener consumes a broadcast at most once (shofarSeenRef dedupe).
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled || pendingShofarRef.current) return;
      try {
        const { data } = await api.get("/shofar/active-broadcast");
        const bcast = data?.broadcast;
        if (!bcast || !bcast.broadcast_id) return;
        if (shofarSeenRef.current.has(bcast.broadcast_id)) return;
        pendingShofarRef.current = bcast;
      } catch { /* swallow */ }
    };
    tick();
    const id = setInterval(tick, 15 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // ---- Crossfade orchestrator (Phase B.1) ----
  // Splice/peek logic for "what is the next track?" — encapsulates the
  // commercial-cadence splice so both the natural-end and skip paths share
  // exactly one source of truth. Returns { nextTrack, mutate } where `mutate`
  // is a function the caller invokes once it's ready to commit the queue
  // change (so we can preload audio BEFORE mutating React state).
  const computeNextTrackPlan = useCallback(() => {
    const q = queueRef.current;
    const i = indexRef.current;

    // (A) Highest priority: a pending shofar broadcast — fire at the next
    // clean break regardless of any other pending interjection.
    if (pendingShofarRef.current) {
      const data = pendingShofarRef.current;
      const shofarTrack = {
        id: `shofar-${data.shofar.id}-${data.broadcast_id}`,
        shofar_clip_id: data.shofar.id,
        title: data.shofar.label || "Shofar",
        artist: "Yah-Wave Radio · Shofar broadcast",
        media_type: data.shofar.media_type || "audio",
        is_shofar: true,
        has_cover: false,
        has_cover_video: false,
        duration_seconds: data.shofar.duration_seconds,
        stream_url_override: data.shofar.stream_url,
      };
      const insertIdx = i + 1;
      return {
        nextTrack: shofarTrack,
        mutate: () => {
          shofarSeenRef.current.add(data.broadcast_id);
          pendingShofarRef.current = null;
          const cq = queueRef.current;
          const ci = indexRef.current;
          const at = ci + 1;
          setQueue([...cq.slice(0, at), shofarTrack, ...cq.slice(at)]);
          setIndex(at);
        },
        kind: "shofar-splice",
      };
    }

    // (B) Pending prayer slot — fires at scheduled time, splices a track-
    // attached prayer at the next clean break.
    if (pendingPrayerRef.current) {
      const data = pendingPrayerRef.current;
      const t = data.track;
      const prayerTrack = {
        id: `prayer-${data.slot.id}-${data.fire_key}`,
        prayer_slot_id: data.slot.id,
        title: data.slot.label || t.title,
        artist: t.artist || "Yah-Wave Radio · Prayer",
        media_type: t.media_type || "audio",
        is_prayer: true,
        has_cover: !!t.has_cover,
        has_cover_video: !!t.has_cover_video,
        duration_seconds: t.duration_seconds,
        // Streams via the existing /api/tracks/{id}/stream path.
        backing_track_id: t.id,
      };
      const insertIdx = i + 1;
      return {
        nextTrack: prayerTrack,
        mutate: () => {
          pendingPrayerRef.current = null;
          const cq = queueRef.current;
          const ci = indexRef.current;
          const at = ci + 1;
          setQueue([...cq.slice(0, at), prayerTrack, ...cq.slice(at)]);
          setIndex(at);
        },
        kind: "prayer-splice",
      };
    }

    // (C) Pending commercial trumps the natural next.
    if (pendingCommercialRef.current) {
      const data = pendingCommercialRef.current;
      const commercialTrack = {
        id: `commercial-${data.id}-${Date.now()}`,
        commercial_id: data.id,
        title: `${data.sponsor} · sponsor message`,
        artist: data.cta || "Yah-Wave Radio sponsor",
        media_type: data.media_type || "audio",
        is_commercial: true,
        has_cover: false,
        has_cover_video: false,
        duration_seconds: data.duration_seconds,
        sponsor_url: data.url,
      };
      const insertIdx = i + 1;
      return {
        nextTrack: commercialTrack,
        mutate: () => {
          const cq = queueRef.current;
          const ci = indexRef.current;
          const at = ci + 1;
          setQueue([...cq.slice(0, at), commercialTrack, ...cq.slice(at)]);
          setIndex(at);
        },
        kind: "commercial-splice",
      };
    }

    // (D) Current track is itself an interjection (commercial / prayer /
    // shofar) → remove it from the queue and continue with the next REAL track.
    const cur = trackRef.current;
    const curIsInterjection = cur && (cur.is_commercial || cur.is_prayer || cur.is_shofar);
    if (curIsInterjection) {
      const idx = q.findIndex((x) => x.id === cur.id);
      const newQ = idx === -1 ? q : [...q.slice(0, idx), ...q.slice(idx + 1)];
      const nextIdx = newQ.length === 0 ? 0 : (idx >= newQ.length ? 0 : idx);
      const nextTrack = newQ[nextIdx] || null;
      return {
        nextTrack,
        mutate: () => {
          if (cur.is_commercial) {
            commercialAccumulatedRef.current = 0;
            pendingCommercialRef.current = null;
          }
          setQueue(newQ);
          setIndex(nextIdx);
        },
        kind: "interjection-removal",
      };
    }
    // (E) Normal queue advancement.
    if (q.length === 0) return { nextTrack: null, mutate: () => {}, kind: "noop" };
    const nextIdx = (i + 1) % q.length;
    return {
      nextTrack: q[nextIdx] || null,
      mutate: () => setIndex(nextIdx),
      kind: "advance",
    };
  }, []);

  // Equal-power audio crossfade between the two `<audio>` decks. Returns
  // `true` if a fade actually started (caller should NOT also hard-cut). When
  // the outgoing or incoming track is a video, or when fadeDuration <= 0, or
  // when there's no next track, we skip and return `false` so the caller
  // falls through to the hard-cut path.
  const crossfadeAdvance = useCallback((fadeDuration) => {
    if (stopRequestedRef.current) return false;
    if (!fadeDuration || fadeDuration <= 0) return false;
    if (crossfadeRef.current.armed) return true; // already in flight, do nothing extra
    const outgoing = trackRef.current;
    if (!outgoing) return false;
    if (outgoing.media_type === "video") return false; // video transitions hard-cut

    const plan = computeNextTrackPlan();
    if (!plan.nextTrack) return false;
    if (plan.nextTrack.media_type === "video") return false; // can't fade INTO a video element

    const outgoingDeck = getActiveAudio();
    const incomingDeck = getInactiveAudio();
    if (!outgoingDeck || !incomingDeck) return false;

    // Pre-load incoming on the inactive deck, start at vol 0. Track whether
    // play() actually resolved — if it rejects (autoplay heuristics, media not
    // ready under production latency, etc.) we MUST abort the swap, otherwise
    // we'd advance the queue index against a deck that never started: the UI
    // shows the next track's metadata while audio goes silent.
    const incomingState = { playSettled: false, playFailed: false };
    try {
      incomingDeck.src = trackToSrc(plan.nextTrack);
      incomingDeck.volume = 0;
      incomingDeck.load();
      const p = incomingDeck.play();
      if (p && typeof p.then === "function") {
        p.then(() => { incomingState.playSettled = true; })
         .catch((err) => {
           incomingState.playSettled = true;
           incomingState.playFailed = true;
           // eslint-disable-next-line no-console
           console.warn("[Yahweh Player] crossfade incoming play() rejected:", err?.name || err);
         });
      } else {
        incomingState.playSettled = true;
      }
    } catch (_) { return false; }

    crossfadeRef.current.armed = true;
    const masterVol = volume;
    const startTs = Date.now();
    const incomingChannel = activeAudioChannelRef.current === "A" ? "B" : "A";

    const abortFade = () => {
      // Restore the outgoing deck to full volume and trigger a clean hard-cut
      // advance via the src-load effect (which clears the inactive deck and
      // re-loads/plays on the active deck with proper tap-to-start fallback).
      // This handles BOTH natural-end stalls AND manual-skip failures.
      if (crossfadeRef.current.intervalId) {
        clearInterval(crossfadeRef.current.intervalId);
        crossfadeRef.current.intervalId = null;
      }
      try { outgoingDeck.volume = masterVol; } catch (_) {}
      try { incomingDeck.pause(); incomingDeck.removeAttribute("src"); incomingDeck.load(); incomingDeck.volume = 0; } catch (_) {}
      crossfadeRef.current.armed = false;
      crossfadeRef.current.suppressSrcLoad = false;
      crossfadeRef.current.suppressEnded = false;
      // Advance the queue index — currentTrack.id will change and the
      // src-load effect will reload + play on the active (outgoing) deck.
      plan.mutate();
    };

    const tick = () => {
      const elapsed = (Date.now() - startTs) / 1000;
      const progress = Math.min(1, elapsed / fadeDuration);
      const angle = progress * (Math.PI / 2);
      try { outgoingDeck.volume = masterVol * Math.cos(angle); } catch (_) {}
      try { incomingDeck.volume = masterVol * Math.sin(angle); } catch (_) {}

      // Early abort: if incoming.play() rejected (verdict known mid-fade), stop
      // immediately and roll outgoing back to full volume.
      if (incomingState.playFailed) {
        // eslint-disable-next-line no-console
        console.warn("[Yahweh Player] aborting crossfade — incoming deck failed to start");
        abortFade();
        return;
      }

      if (progress >= 1) {
        clearInterval(crossfadeRef.current.intervalId);
        crossfadeRef.current.intervalId = null;

        // Safety net: if the incoming deck never actually entered playing
        // state by fade-end (paused/stalled/readyState too low), DO NOT swap.
        // Falling back to abort lets the outgoing's natural `ended` event run
        // the hard-cut path, which re-loads on the active deck with the
        // correct retry/tap-to-start UX.
        const incomingNotPlaying =
          incomingDeck.paused ||
          incomingState.playFailed ||
          incomingDeck.readyState < 2; // HAVE_CURRENT_DATA
        if (incomingNotPlaying) {
          // eslint-disable-next-line no-console
          console.warn("[Yahweh Player] crossfade swap aborted at fade-end — incoming not playing", {
            paused: incomingDeck.paused, readyState: incomingDeck.readyState,
          });
          abortFade();
          return;
        }

        // Swap decks: outgoing pauses + clears, incoming becomes the active
        // deck. Suppress the src effect (incoming is already loaded + playing)
        // and the late `ended` event from the outgoing track (we already
        // advanced).
        try { outgoingDeck.pause(); outgoingDeck.removeAttribute("src"); outgoingDeck.load(); outgoingDeck.volume = 0; } catch (_) {}
        try { incomingDeck.volume = masterVol; } catch (_) {}
        activeAudioChannelRef.current = incomingChannel;
        setActiveAudioChannel(incomingChannel);
        crossfadeRef.current.suppressSrcLoad = true;
        crossfadeRef.current.suppressEnded = true;
        crossfadeRef.current.armed = false;
        plan.mutate();
      }
    };
    crossfadeRef.current.intervalId = setInterval(tick, CROSSFADE_FRAME_MS);
    // Run an immediate tick so vol=0/master snap-on doesn't audibly click.
    tick();
    return true;
  }, [computeNextTrackPlan, getActiveAudio, getInactiveAudio, trackToSrc, volume]);

  // Wrap `next()` so manual Skip uses the (shorter) skip fade when applicable.
  const nextWithFade = useCallback(() => {
    const fade = Math.min(SKIP_FADE_SECONDS, listeningPrefs.crossfade_seconds || 0);
    if (crossfadeAdvance(fade)) return;
    setIndex((i) => (queueRef.current.length === 0 ? 0 : (i + 1) % queueRef.current.length));
  }, [crossfadeAdvance, listeningPrefs.crossfade_seconds]);

  // Auto-arm crossfade as the active track approaches its natural end.
  useEffect(() => {
    if (!isPlaying || !currentTrack) return undefined;
    const cf = listeningPrefs.crossfade_seconds || 0;
    if (cf <= 0) return undefined;
    if (currentTrack.media_type === "video") return undefined; // video tracks hard-cut
    const id = setInterval(() => {
      if (crossfadeRef.current.armed || stopRequestedRef.current) return;
      const el = getActive();
      if (!el || !el.duration || isNaN(el.duration) || el.duration === Infinity) return;
      const remaining = el.duration - el.currentTime;
      if (remaining > 0 && remaining <= cf) {
        crossfadeAdvance(cf);
      }
    }, 250);
    return () => clearInterval(id);
  }, [isPlaying, currentTrack, listeningPrefs.crossfade_seconds, getActive, crossfadeAdvance]);

  // "Up Next" preview — surfaces the planned next track during the last ~5s
  // of the active one so listeners get a subtle heads-up of what's coming.
  // Independent of crossfade settings (works even at 0s crossfade). Cleared
  // whenever the active track changes (including after the swap) so it
  // doesn't briefly show "Up next: <self>".
  const UP_NEXT_WINDOW_SECONDS = 5;
  useEffect(() => { setUpNextTrack(null); }, [currentTrack?.id]);
  useEffect(() => {
    if (!isPlaying || !currentTrack) return undefined;
    if (currentTrack.is_commercial || currentTrack.is_shofar) return undefined;
    const id = setInterval(() => {
      const el = getActive();
      if (!el || !el.duration || isNaN(el.duration) || el.duration === Infinity) return;
      const remaining = el.duration - el.currentTime;
      if (remaining > 0 && remaining <= UP_NEXT_WINDOW_SECONDS) {
        const plan = computeNextTrackPlan();
        const nxt = plan.nextTrack;
        // Only set if we have a real next-track candidate AND it's not the same
        // as the currently-playing one (handles single-track loops gracefully).
        if (nxt && nxt.id && nxt.id !== currentTrack.id) {
          setUpNextTrack((prev) => (prev && prev.id === nxt.id ? prev : nxt));
        }
      } else if (remaining > UP_NEXT_WINDOW_SECONDS + 0.5) {
        // Scrubbed back outside the window — hide the pill until we re-enter.
        setUpNextTrack((prev) => (prev ? null : prev));
      }
    }, 500);
    return () => clearInterval(id);
  }, [isPlaying, currentTrack, getActive, computeNextTrackPlan]);

  // Record each real track to the recently-played localStorage when it
  // becomes the active track. Skips interjections (commercials, shofars,
  // prayers) so they don't pollute the anti-repeat memory. Used by the radio
  // queue refill below to send `?exclude=...` to the backend.
  useEffect(() => {
    if (!currentTrack || !currentTrack.id) return;
    if (currentTrack.is_commercial || currentTrack.is_shofar || currentTrack.is_prayer) return;
    noteRecentlyPlayed(currentTrack.id);
  }, [currentTrack?.id]);

  // Auto-replenish the live-radio queue when the listener gets within 8
  // tracks of the end. Fetches a fresh anti-clump-shuffled batch from
  // /api/radio/queue, excluding tracks the listener heard in the last few
  // hours, then APPENDS to the queue (de-duped against what's already
  // queued ahead so we never re-queue an upcoming track). Triggers ONLY in
  // mode === "radio" so it doesn't interfere with playlists, stations,
  // direct track plays, etc.
  const radioRefillInFlightRef = useRef(false);
  useEffect(() => {
    if (mode !== "radio") return;
    if (!queue.length) return;
    const tracksAhead = queue.length - index - 1;
    if (tracksAhead > 8) return;
    if (radioRefillInFlightRef.current) return;
    radioRefillInFlightRef.current = true;
    (async () => {
      try {
        const upcomingIds = new Set(queue.slice(index).map((t) => t.id));
        const exclude = recentlyPlayedIds().join(",");
        const resp = await api.get("/radio/queue", {
          params: { count: 60, ...(exclude ? { exclude } : {}) },
        });
        const fresh = (resp.data || []).filter((t) => t && t.id && !upcomingIds.has(t.id));
        if (fresh.length) {
          setQueue((prev) => [...prev, ...fresh]);
        }
      } catch (_) {
        // Silent failure — listener stays on the existing queue, which loops.
      } finally {
        radioRefillInFlightRef.current = false;
      }
    })();
  }, [mode, queue, index]);

  const togglePlay = useCallback(() => {
    const el = getActive();
    if (!el || !currentTrack || !el.currentSrc) return;
    if (el.paused) {
      stopRequestedRef.current = false;  // user resumed — clear stop guard
      el.play().then(() => setNeedsTapToStart(false)).catch(() => {});
    } else {
      el.pause();
    }
  }, [currentTrack, getActive]);

  const seek = useCallback((t) => {
    const el = getActive();
    if (el && !isNaN(t) && el.currentSrc) el.currentTime = t;
  }, [getActive]);

  const setVolume = useCallback((v) => setVolumeState(v), []);

  // Hard stop: pause, rewind, drop the queue, kill MediaSession, and arm the
  // auto-next guard so a late `ended` event doesn't bring playback back.
  const stopPlayback = useCallback(() => {
    stopRequestedRef.current = true;
    // Cancel any in-flight interjection cadence so a pending sponsor / prayer /
    // shofar doesn't splice itself back into the queue after Stop.
    pendingCommercialRef.current = null;
    commercialAccumulatedRef.current = 0;
    pendingPrayerRef.current = null;
    pendingShofarRef.current = null;
    // Cancel any in-flight crossfade — Stop is instant.
    if (crossfadeRef.current.intervalId) {
      clearInterval(crossfadeRef.current.intervalId);
    }
    crossfadeRef.current = {
      armed: false, intervalId: null, suppressEnded: false, suppressSrcLoad: false,
    };
    stopToneLayer();
    const a1 = audioARef.current; const a2 = audioBRef.current;
    const v = videoRef.current; const cv = coverRef.current;
    [a1, a2, v].forEach((el) => {
      if (!el) return;
      try {
        el.pause();
        el.currentTime = 0;
        el.removeAttribute("src");
        el.load();
        // Reset deck volume so a subsequent fade-in doesn't inherit the
        // previous fade's gain. Audibly silent because paused, but keeps the
        // DevTools snapshot consistent with the "stopped" state.
        el.volume = 0;
      } catch (_) {}
    });
    activeAudioChannelRef.current = "A";
    setActiveAudioChannel("A");
    if (cv) {
      try { cv.pause(); cv.removeAttribute("src"); cv.load(); } catch (_) {}
    }
    setQueue([]); setIndex(0);
    setIsPlaying(false);
    setCurrentTime(0); setDuration(0);
    setPlaybackError(null); setNeedsTapToStart(false);
    setStationId(null); setStationName(null);
    setExpanded(false); setVideoHidden(false);
    if (typeof navigator !== "undefined" && "mediaSession" in navigator) {
      try {
        navigator.mediaSession.metadata = null;
        navigator.mediaSession.playbackState = "none";
      } catch (_) {}
    }
  }, []);

  // Backwards-compat alias
  const stop = stopPlayback;

  const expand = useCallback(() => { setExpanded(true); setVideoHidden(false); }, []);
  const collapse = useCallback(() => setExpanded(false), []);
  const hideVisual = useCallback(() => { setVideoHidden(true); setExpanded(false); }, []);
  const showVisual = useCallback(() => setVideoHidden(false), []);
  const dismissError = useCallback(() => setPlaybackError(null), []);

  // ---- Reactions: load my likes/dislikes once we have a track playing ----
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // Lazy-load axios via dynamic import to avoid circular
        const { api: apiClient } = await import("../lib/api");
        const { data } = await apiClient.get("/reactions/me");
        if (mounted) setReactions(data || {});
      } catch { /* anonymous user — ignore */ }
    })();
    return () => { mounted = false; };
  }, []);

  const sendReaction = useCallback(async (trackId, type) => {
    try {
      const { api: apiClient } = await import("../lib/api");
      await apiClient.post("/reactions", { track_id: trackId, type });
    } catch (e) { /* swallow — reactions are best-effort */ }
  }, []);

  const thumbUp = useCallback(() => {
    const t = trackRef.current;
    if (!t) return;
    setReactions((prev) => {
      const cur = prev[t.id];
      const nextState = { ...prev };
      if (cur === "like") delete nextState[t.id]; // toggle off
      else nextState[t.id] = "like";
      return nextState;
    });
    sendReaction(t.id, "like");
  }, [sendReaction]);

  const thumbDown = useCallback(() => {
    const t = trackRef.current;
    if (!t) return;
    setReactions((prev) => ({ ...prev, [t.id]: "dislike" }));
    sendReaction(t.id, "dislike");
    // Auto-skip
    setTimeout(() => next(), 200);
  }, [sendReaction, next]);

  // Log a "play" event when a new track starts (debounced via track id change)
  useEffect(() => {
    if (currentTrack?.id && !currentTrack?.is_commercial && !currentTrack?.is_prayer && !currentTrack?.is_shofar) {
      sendReaction(currentTrack.id, "play");
    }
  }, [currentTrack?.id, currentTrack?.is_commercial, currentTrack?.is_prayer, currentTrack?.is_shofar, sendReaction]);

  // ---- Media Session API (lock screen / notification / OS controls) ----
  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
    if (!currentTrack) {
      try { navigator.mediaSession.metadata = null; } catch (_) {}
      return;
    }
    try {
      const artwork = [];
      if (currentTrack.has_cover) {
        const url = `${BACKEND_URL}/api/tracks/${currentTrack.id}/cover`;
        artwork.push(
          { src: url, sizes: "96x96", type: "image/jpeg" },
          { src: url, sizes: "192x192", type: "image/jpeg" },
          { src: url, sizes: "512x512", type: "image/jpeg" },
        );
      }
      // eslint-disable-next-line no-undef
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: currentTrack.title || "",
        artist: currentTrack.artist || "",
        album: stationName || "Yah-Wave Radio",
        artwork,
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[Yahweh Player] could not set MediaMetadata", e);
    }

    const safeSet = (action, handler) => {
      try { navigator.mediaSession.setActionHandler(action, handler); } catch (_) {}
    };
    safeSet("play", () => {
      stopRequestedRef.current = false;
      const el = getActive(); if (el && el.paused) el.play().then(() => setNeedsTapToStart(false)).catch(() => {});
    });
    safeSet("pause", () => {
      const el = getActive(); if (el && !el.paused) el.pause();
    });
    safeSet("stop", () => stopPlayback());
    safeSet("previoustrack", () => prev());
    safeSet("nexttrack", () => nextWithFade());
    safeSet("seekto", (details) => {
      if (details && typeof details.seekTime === "number") seek(details.seekTime);
    });
    safeSet("seekforward", (details) => {
      const el = getActive(); if (!el) return;
      const offset = (details && details.seekOffset) || 10;
      seek(Math.min((el.currentTime || 0) + offset, el.duration || 0));
    });
    safeSet("seekbackward", (details) => {
      const el = getActive(); if (!el) return;
      const offset = (details && details.seekOffset) || 10;
      seek(Math.max((el.currentTime || 0) - offset, 0));
    });
  }, [currentTrack, getActive, nextWithFade, prev, seek, stopPlayback]);

  // Reflect playback state for OS control highlight
  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
    if (!currentTrack) {
      // Nothing to play — keep playbackState as 'none' so OS lock-screen shows stopped icon.
      try { navigator.mediaSession.playbackState = "none"; } catch (_) {}
      return;
    }
    try { navigator.mediaSession.playbackState = isPlaying ? "playing" : "paused"; } catch (_) {}
  }, [isPlaying, currentTrack]);

  // Keep position state fresh so lock-screen scrubber works
  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
    if (!duration || isNaN(duration) || duration === Infinity) return;
    try {
      navigator.mediaSession.setPositionState({
        duration,
        position: Math.min(currentTime || 0, duration),
        playbackRate: 1,
      });
    } catch (_) {}
  }, [duration, currentTime]);

  // ---- Listening preferences (Frequency / Tuning) ----
  // Load saved prefs on mount; re-load if auth state changes (token toggled).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const p = await loadPrefs();
      if (!cancelled) setListeningPrefs(clampPrefs(p));
    })();
    return () => { cancelled = true; };
  }, []);

  // Apply pitch tuning live to the active media element. We use playbackRate +
  // preservesPitch=false because true tempo-preserving pitch shift requires an
  // AudioWorklet phase vocoder. The 1.8% tempo drift at 432 Hz is imperceptible.
  useEffect(() => {
    const a1 = audioARef.current; const a2 = audioBRef.current; const v = videoRef.current;
    const rate = playbackRateFromA4(listeningPrefs.a4_hz);
    [a1, a2, v].forEach((el) => {
      if (!el) return;
      try {
        // Some browsers spell this differently; setting all known names is safe.
        el.preservesPitch = false;
        if ("mozPreservesPitch" in el) el.mozPreservesPitch = false;
        if ("webkitPreservesPitch" in el) el.webkitPreservesPitch = false;
        el.playbackRate = rate;
      } catch (_e) { /* unsupported — fall back to original mix */ }
    });
  }, [listeningPrefs.a4_hz, currentTrack?.id]);

  // ---------- Live-radio network recovery ----------
  // When a network blip kills the radio stream (audio element fires `error`,
  // or the device goes offline), auto-rejoin once connectivity returns. Two
  // triggers in tandem:
  //   1. `window.online` event — fires immediately when the OS regains
  //      connectivity (laptop wakes, phone back in signal).
  //   2. Periodic retry every 4s while `playbackError` is set AND we're in
  //      radio mode — covers cases where the error fires but `online` never
  //      flips (e.g. backend storage hiccup, CDN 5xx, brief DNS failure).
  // Recovery: reload the SAME track on the active deck and call play(). If
  // the retry succeeds the next `canplay` event clears `playbackError`.
  const recoveryTimerRef = useRef(null);
  const recoveryAttemptRef = useRef(0);
  useEffect(() => {
    if (mode !== "radio") return undefined;
    if (!playbackError) {
      // Cleared — reset attempt counter and let the canplay handler do its thing.
      recoveryAttemptRef.current = 0;
      return undefined;
    }
    const retry = () => {
      const t = trackRef.current;
      const el = getActive();
      if (!t || !el) return;
      recoveryAttemptRef.current += 1;
      // eslint-disable-next-line no-console
      console.warn("[Yahweh Player] radio recovery attempt", {
        attempt: recoveryAttemptRef.current,
        online: navigator.onLine,
        track_id: t.id,
      });
      try {
        el.src = trackToSrc(t);
        el.volume = volume;
        el.load();
        const p = el.play();
        if (p && typeof p.then === "function") {
          p.then(() => { setPlaybackError(null); })
           .catch(() => { /* swallow — periodic timer will retry */ });
        }
      } catch (_) { /* swallow */ }
    };
    // Try immediately, then again on a steady cadence until canplay clears
    // playbackError. Cap attempts at 30 (~2 minutes) to avoid burning battery
    // on a permanently dead connection.
    retry();
    recoveryTimerRef.current = setInterval(() => {
      if (recoveryAttemptRef.current >= 30) {
        if (recoveryTimerRef.current) {
          clearInterval(recoveryTimerRef.current);
          recoveryTimerRef.current = null;
        }
        return;
      }
      retry();
    }, 4000);
    const onOnline = () => {
      // eslint-disable-next-line no-console
      console.warn("[Yahweh Player] network online — forcing radio recovery");
      retry();
    };
    window.addEventListener("online", onOnline);
    return () => {
      if (recoveryTimerRef.current) {
        clearInterval(recoveryTimerRef.current);
        recoveryTimerRef.current = null;
      }
      window.removeEventListener("online", onOnline);
    };
  }, [mode, playbackError, getActive, trackToSrc, volume]);

  // Tone layer lifecycle — only run while a track is loaded AND the user has
  // interacted (audio context resumed). Stop on Stop or when tone_hz is 0.
  useEffect(() => {
    if (!currentTrack) { stopToneLayer(); return; }
    if (!listeningPrefs.tone_hz || listeningPrefs.tone_hz <= 0) {
      stopToneLayer();
      return;
    }
    if (!isPlaying) {
      // Don't start a tone the user isn't actively listening to. Mute if running.
      setToneLayerVolume(0);
      return;
    }
    resumeAudioContextOnGesture();
    startToneLayer(listeningPrefs.tone_hz, listeningPrefs.tone_volume);
  }, [
    currentTrack,
    isPlaying,
    listeningPrefs.tone_hz,
    listeningPrefs.tone_volume,
  ]);

  const updateListeningPrefs = useCallback((partial) => {
    setListeningPrefs((prev) => {
      const merged = clampPrefs({ ...prev, ...partial });
      savePrefs(merged);
      return merged;
    });
  }, []);

  const setListeningPreset = useCallback((presetKey) => {
    const next = presetToPrefs(presetKey);
    setListeningPrefs(next);
    savePrefs(next);
  }, []);

  // ---- Continue Listening: persist position so user can resume ----
  // Saves: every 15s while playing, on pause, on track change (saves the
  // *previous* track state), and on tab close. Position 0 is filtered in lib.
  const lastSavedRef = useRef({ trackId: null, position: 0 });
  const saveCurrent = useCallback(() => {
    const t = trackRef.current;
    const el = getActive();
    if (!t || !el) return;
    // Don't persist interjection tracks (commercial / prayer / shofar) — listeners
    // want to resume the music they were listening to, not the sponsor or shofar.
    if (t.is_commercial || t.is_prayer || t.is_shofar) return;
    const pos = el.currentTime || 0;
    // De-dupe: skip if same track and we already saved within ~3s of this position
    const last = lastSavedRef.current;
    if (last.trackId === t.id && Math.abs(last.position - pos) < 3) return;
    lastSavedRef.current = { trackId: t.id, position: pos };
    saveContinueState({
      track: t,
      stationId,
      stationName,
      mode,
      positionSeconds: pos,
    });
  }, [getActive, stationId, stationName, mode]);

  useEffect(() => {
    if (!isPlaying) return;
    const id = setInterval(saveCurrent, 15000);
    return () => clearInterval(id);
  }, [isPlaying, saveCurrent]);

  // Save on pause (immediate snapshot of where they stopped).
  useEffect(() => {
    if (!isPlaying && trackRef.current) saveCurrent();
  }, [isPlaying, saveCurrent]);

  // Save on tab close / hidden — best-effort.
  useEffect(() => {
    const onHide = () => saveCurrent();
    window.addEventListener("pagehide", onHide);
    window.addEventListener("beforeunload", onHide);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") saveCurrent();
    });
    return () => {
      window.removeEventListener("pagehide", onHide);
      window.removeEventListener("beforeunload", onHide);
    };
  }, [saveCurrent]);

  const isVideo = currentTrack?.media_type === "video";
  const hasCoverVideo = !!currentTrack?.has_cover_video && currentTrack?.media_type !== "video";
  // Legacy alias: components reading `audioRef` from context get the
  // currently-active deck so the visualizer follows the audible source.
  const audioRef = activeAudioChannel === "B" ? audioBRef : audioARef;

  return (
    <PlayerContext.Provider value={{
      queue, index, currentTrack, isPlaying, currentTime, duration, volume, mode,
      isVideo, hasCoverVideo, expanded, videoHidden, playbackError, needsTapToStart,
      stationId, stationName, reactions, upNextTrack,
      attachAudio, attachAudioA, attachAudioB, attachVideo, attachCover,
      setVideoHost, videoHost,
      audioRef, audioARef, audioBRef, videoRef,
      activeAudioChannel,
      playTrack, playQueue, togglePlay, next: nextWithFade, prev, seek, setVolume, stop, stopPlayback,
      playTrackAt,
      listeningPrefs, updateListeningPrefs, setListeningPreset,
      expand, collapse, hideVisual, showVisual, dismissError,
      thumbUp, thumbDown,
    }}>
      {children}
    </PlayerContext.Provider>
  );
}

export const usePlayer = () => useContext(PlayerContext);
